// src/pages/Promesa.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge
} from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import AppNavbar from '../components/AppNavbar';
import BottomNav from '../components/BottomNav';
import './Promesa.css';

interface Servicio {
  id: number;
  tiposervicio: string;
  smartolt?: string;
}

interface ClienteData {
  id: number;
  nombre: string;
  cedula: string;
  estado: string;
  Promesa_Pago?: string; // 👈 AÑADIDO
  facturacion?: { total_facturas: number | string };
  servicios: Servicio[];
}

interface FacturaData {
  id: number;
  estado: string;
}

// Parser para cadenas PHP-serialized
function parsePHPSerialized(str: string): Record<string, string> {
  const obj: Record<string, string> = {};
  const regex = /s:\d+:"([^\\"]+)";s:\d+:"([^"]*)";/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(str))) {
    obj[match[1]] = match[2];
  }
  return obj;
}

// Días transcurridos desde una fecha "YYYY-MM-DD" hasta hoy (positivo = en el pasado).
// Devuelve null si la fecha está vacía o es inválida (0000-00-00).
function diasDesde(fechaStr?: string): number | null {
  if (!fechaStr || fechaStr.startsWith('0000')) return null;
  const soloFecha = fechaStr.split(' ')[0];
  const partes = soloFecha.split('-').map(Number);
  if (partes.length !== 3 || partes.some(isNaN)) return null;
  const [a, m, d] = partes;
  const fecha = new Date(a, m - 1, d);
  const hoy = new Date();
  const ms = hoy.getTime() - fecha.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

// Suma N días a una fecha "YYYY-MM-DD" y devuelve "DD/MM/YYYY"
function sumarDias(fechaStr: string, dias: number): string {
  const soloFecha = fechaStr.split(' ')[0];
  const [a, m, d] = soloFecha.split('-').map(Number);
  const fecha = new Date(a, m - 1, d);
  fecha.setDate(fecha.getDate() + dias);
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(fecha.getDate())}/${p2(fecha.getMonth() + 1)}/${fecha.getFullYear()}`;
}

// Cada cuántos días se puede otorgar una nueva promesa de pago (contados desde
// la fecha "Promesa vigente hasta" de la anterior). Un solo lugar para cambiarlo.
const DIAS_ENTRE_PROMESAS = 22;

// Obtener nombre del técnico desde localStorage (sin tocar Menu)
function getTecnicoNombre(): string {
  const keys = ['clientData', 'userData', 'authUser', 'sessionUser', 'usuario'];

  for (const key of keys) {
    const raw = localStorage.getItem(key);
    if (!raw) continue;

    // Si está guardado como string plano
    if (raw.length < 140 && !raw.trim().startsWith('{') && !raw.trim().startsWith('[')) {
      return raw.trim();
    }

    // Si está guardado como JSON
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.nombre === 'string' && parsed.nombre.trim()) return parsed.nombre.trim();
      if (typeof parsed?.user?.nombre === 'string' && parsed.user.nombre.trim()) return parsed.user.nombre.trim();
      if (typeof parsed?.datos?.nombre === 'string' && parsed.datos.nombre.trim()) return parsed.datos.nombre.trim();
      if (typeof parsed?.cliente_nombre === 'string' && parsed.cliente_nombre.trim()) return parsed.cliente_nombre.trim();
    } catch {
      // ignorar
    }
  }

  return '';
}

// Limpia el mensaje (quita asteriscos y espacios)
function cleanMsg(x: any): string {
  return String(x ?? '').replace(/\*/g, '').trim();
}

// Extrae el mensaje de error real de tu API_PROMESA (array u objeto)
function extractPromesaError(payload: any): string {
  // Caso: Array [{ estado, mensaje_Promesa }]
  if (Array.isArray(payload) && payload.length) {
    const r = payload[0];
    const estado = String(r?.estado ?? '').toLowerCase();
    if (estado === 'error') return cleanMsg(r?.mensaje_Promesa || r?.mensaje);
  }

  // Caso: Objeto { estado, mensaje_Promesa }
  if (payload && typeof payload === 'object') {
    const estado = String(payload?.estado ?? '').toLowerCase();
    if (estado === 'error') return cleanMsg(payload?.mensaje_Promesa || payload?.mensaje);
  }

  return '';
}

const Promesa: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tecnicoNombre = getTecnicoNombre();

  // Estados base
  const [cedula, setCedula] = useState('');
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [errorCliente, setErrorCliente] = useState('');
  const [cliente, setCliente] = useState<ClienteData | null>(null);

  // Factura / Promesa
  const [factura, setFactura] = useState<FacturaData | null>(null);
  const [loadingFactura, setLoadingFactura] = useState(false);
  const [errorFactura, setErrorFactura] = useState('');

  const [loadingPromesa, setLoadingPromesa] = useState(false);
  const [errorPromesa, setErrorPromesa] = useState('');
  const [successPromesa, setSuccessPromesa] =
    useState<{ mensaje: string; fecha: string; descripcion: string } | null>(null);

  // Respuesta del webhook
  const [webhookResponse, setWebhookResponse] = useState<any>(null);

  // Recarga de datos del cliente tras activar (para confirmar si ya se activó)
  const [recargandoEstado, setRecargandoEstado] = useState(false);

  // Cuenta regresiva: la ventana se cierra sola a los 90 segundos y vuelve al Menú
  const [segundosRestantes, setSegundosRestantes] = useState(90);

  // SmartOLT parsing
  const [smartoltJson, setSmartoltJson] = useState<Record<string, string> | null>(null);
  const onuExternalId = smartoltJson?.onu_external_id ?? '';
  const serialOnu = smartoltJson?.sn ?? '';

  // URLs via N8N proxy (sin tokens en el APK)
  const API_CLIENTE    = process.env.REACT_APP_N8N_URL_CLIENTE_CEDULA!;
  const API_FACTURAS   = process.env.REACT_APP_N8N_URL_FACTURAS_CLIENTE!;
  const API_PROMESA    = process.env.REACT_APP_API_URL_PROMESA!;
  const WEBHOOK_PROMESA = process.env.REACT_APP_PROMESA_WEBHOOK!;

  function resetAll() {
    setCedula('');
    setCliente(null);
    setFactura(null);
    setErrorCliente('');
    setErrorFactura('');
    setErrorPromesa('');
    setSuccessPromesa(null);
    setWebhookResponse(null);
    setSmartoltJson(null);
  }

  function resetAlerts() {
    setErrorPromesa('');
    setSuccessPromesa(null);
    setWebhookResponse(null);
  }

  // Si llegamos desde la ficha del cliente (admin) con una cédula, la consultamos automáticamente
  useEffect(() => {
    const cedulaNav = (location.state as { cedula?: string } | undefined)?.cedula;
    if (cedulaNav) consultarCliente(cedulaNav);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuenta regresiva de 90 s: al llegar a 0 cierra la ventana y regresa al Menú
  useEffect(() => {
    const intervalo = setInterval(() => {
      setSegundosRestantes(s => {
        if (s <= 1) {
          clearInterval(intervalo);
          navigate('/menu');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1) Buscar cliente
  async function handleConsulta(e: React.FormEvent) {
    e.preventDefault();
    consultarCliente(cedula);
  }

  async function consultarCliente(ced: string) {
    if (!ced?.trim()) return;
    resetAll();
    setCedula(ced);
    setLoadingCliente(true);

    try {
      const res = await fetch(API_CLIENTE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cedula: ced })
      });

      if (!res.ok) throw new Error('No se encontró el cliente. Verifica la cédula e inténtalo nuevamente.');

      const json = await res.json();
      const datos: ClienteData[] = json?.datos || [];

      if (!datos.length) throw new Error('Cliente no encontrado. Revisa el número ingresado.');

      const cli = datos[0];
      setCliente(cli);

      // Extraer SmartOLT si existe
      const svc = cli.servicios.find(s => s.smartolt);
      if (svc?.smartolt) setSmartoltJson(parsePHPSerialized(svc.smartolt));

      await fetchFacturas(cli.id);
    } catch (err: any) {
      setErrorCliente(err?.message || 'Error consultando cliente.');
    } finally {
      setLoadingCliente(false);
    }
  }

  // Una sola consulta de cliente por cédula; devuelve el cliente o null.
  async function fetchClientePorCedula(ced: string): Promise<ClienteData | null> {
    try {
      const res = await fetch(API_CLIENTE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cedula: ced })
      });
      if (!res.ok) return null;
      const json = await res.json();
      const datos: ClienteData[] = json?.datos || [];
      return datos.length ? datos[0] : null;
    } catch {
      return null;
    }
  }

  // Recarga los datos del cliente tras la promesa y REINTENTA (polling) hasta que
  // el servicio quede ACTIVO o se agoten los intentos. Actualiza la pantalla en vivo.
  async function recargarClienteConReintentos(ced: string) {
    if (!ced?.trim()) return;
    setRecargandoEstado(true);
    const MAX_INTENTOS = 5;      // ~ 5 consultas
    const ESPERA_MS = 5000;      // cada 5 s
    try {
      for (let intento = 1; intento <= MAX_INTENTOS; intento++) {
        const cli = await fetchClientePorCedula(ced);
        if (cli) setCliente(cli);
        if (cli && cli.estado.toLowerCase() === 'activo') break; // ya se activó → salir
        if (intento < MAX_INTENTOS) {
          await new Promise(r => setTimeout(r, ESPERA_MS));
        }
      }
    } finally {
      setRecargandoEstado(false);
    }
  }

  // 2) Buscar facturas
  async function fetchFacturas(idcliente: number) {
    setLoadingFactura(true);
    try {
      const res = await fetch(API_FACTURAS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idcliente, limit: 1, estado: '1' })
      });

      if (!res.ok) throw new Error('No se pudo consultar la factura. Intenta nuevamente.');

      const json = await res.json();
      const facturas: FacturaData[] = json?.facturas || [];

      if (!facturas.length) throw new Error('Este cliente no tiene facturas pendientes en este momento.');

      setFactura(facturas[0]);
    } catch (err: any) {
      setErrorFactura(err?.message || 'Error consultando factura.');
    } finally {
      setLoadingFactura(false);
    }
  }

  // 3) Registrar promesa y notificar webhook
  async function handlePromesa(dias: number) {
    if (!factura || !cliente) return;

    resetAlerts();
    setLoadingPromesa(true);

    const fecha = new Date();
    fecha.setDate(fecha.getDate() + dias);
    const fechalimite = fecha.toISOString().split('T')[0];

    // ✅ Descripción corta (como pediste)
    const descripcion = `Plazo ${dias} día${dias > 1 ? 's' : ''} - ${tecnicoNombre}`;

    try {
      const res = await fetch(API_PROMESA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idfactura: factura.id,
          idcliente: cliente.id,
          fechalimite,
          descripcion
        })
      });

      // Intentar leer JSON; si falla, leer texto
      let data: any = null;
      let rawText = '';

      try {
        data = await res.json();
      } catch {
        try {
          rawText = await res.text();
        } catch {
          rawText = '';
        }
      }

      // ✅ Extraer mensaje real del API (si vino JSON)
      const apiMsg = data ? extractPromesaError(data) : '';

      // ✅ Si el servidor devolvió error HTTP, mostrar mensaje real si existe
      if (!res.ok) {
        const msg = apiMsg || cleanMsg(rawText) || 'No se pudo registrar la promesa.';
        throw new Error(msg);
      }

      // ✅ Si HTTP fue OK pero el backend indica error (tu caso típico)
      if (apiMsg) {
        throw new Error(apiMsg);
      }

      // ✅ Compatibilidad si llega objeto con estado != exito
      if (data?.estado && String(data.estado).toLowerCase() !== 'exito') {
        throw new Error(cleanMsg(data?.mensaje) || 'No se pudo registrar la promesa.');
      }

      // ✅ Éxito (si data trae mensaje, lo usamos; si no, genérico)
      setSuccessPromesa({
        mensaje: cleanMsg(data?.mensaje) || 'Promesa registrada correctamente.',
        fecha: fechalimite,
        descripcion
      });

      // ✅ Tras registrar la promesa, recargar los datos del cliente y reintentar
      // hasta confirmar la activación. Se dispara SIEMPRE (aunque el webhook falle).
      setTimeout(() => { recargarClienteConReintentos(cliente.cedula); }, 3000);

      // Webhook
      const webhookRes = await fetch(WEBHOOK_PROMESA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          onu_external_id: Number(onuExternalId),
          idcliente: cliente.id,
          nombre: cliente.nombre,
          tecnico: tecnicoNombre
        })
      });

      try {
        const webhookJson = await webhookRes.json();
        setWebhookResponse(webhookJson);
      } catch {
        setWebhookResponse({ ok: true, info: 'Webhook ejecutado (sin JSON de respuesta).' });
      }
    } catch (err: any) {
      // ✅ Aquí garantizamos que SIEMPRE haya texto
      const msg = cleanMsg(err?.message) || 'No se pudo registrar la promesa.';
      setErrorPromesa(msg);
    } finally {
      setLoadingPromesa(false);
    }
  }

  const totalFacturas = cliente?.facturacion ? Number(cliente.facturacion.total_facturas) : 0;

  return (
    <>
      <AppNavbar />
      <Container className="mt-4 mb-5">
      <Card className="mx-auto" style={{ maxWidth: 560, border: 'none', borderRadius: 18, boxShadow: 'var(--sn-sombra)', overflow: 'hidden' }}>
        {/* Encabezado con degradado */}
        <div style={{ background: 'linear-gradient(135deg,var(--sn-accion-hover),var(--sn-accion))', color: 'var(--sn-accion-texto)', padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'var(--sn-accion-texto)', borderRadius: 999, padding: '5px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <FaArrowLeft size={12} /> Volver
            </button>
            <span style={{
              fontSize: 12, fontWeight: 700, background: segundosRestantes <= 15 ? 'var(--sn-peligro)' : 'rgba(255,255,255,0.22)',
              borderRadius: 999, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
            }}>
              ⏳ {segundosRestantes}s
            </span>
          </div>
          <div style={{ marginTop: 12, fontSize: 19, fontWeight: 800 }}>
            👋 Promesa de pago
          </div>
          <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
            {tecnicoNombre ? `Gestión: ${tecnicoNombre}` : 'Gestión de promesas de pago'}
          </div>
        </div>

        <Card.Body style={{ padding: '18px 20px' }}>
          {!cliente && (
            <p className="text-muted mb-4" style={{ fontSize: 13 }}>
              Ingresa la <strong>cédula del cliente</strong> para continuar.
            </p>
          )}

          {!cliente ? (
            <Form onSubmit={handleConsulta} className="mb-4">
              <Form.Group controlId="cedula" className="mb-3">
                <Form.Label>📌 Cédula del cliente</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ej: 1312908286"
                  value={cedula}
                  onChange={e => setCedula(e.target.value)}
                  required
                />
                <Form.Text className="text-muted">
                  Consejo: escribe la cédula sin espacios ni guiones.
                </Form.Text>
              </Form.Group>

              <Button type="submit" disabled={loadingCliente}>
                {loadingCliente ? <Spinner animation="border" size="sm" /> : '🔎 Consultar cliente'}
              </Button>

              {errorCliente && (
                <Alert variant="danger" className="mt-3">
                  ⚠️ {errorCliente}
                </Alert>
              )}
            </Form>
          ) : (
            <>
              <div style={{ background: 'var(--sn-superficie-2)', border: '1px solid var(--sn-borde)', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--sn-texto)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    👤 {cliente.nombre}
                  </span>
                  <Badge bg={cliente.estado.toLowerCase() === 'activo' ? 'success' : 'danger'} style={{ flexShrink: 0 }}>
                    {cliente.estado}
                  </Badge>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: 13 }}>
                  <div><span style={{ color: 'var(--sn-texto-3)' }}>Cédula</span><br /><strong>{cliente.cedula}</strong></div>
                  <div><span style={{ color: 'var(--sn-texto-3)' }}>ID Cliente</span><br /><strong>{cliente.id}</strong></div>
                  <div><span style={{ color: 'var(--sn-texto-3)' }}>Total facturas</span><br /><strong style={{ color: totalFacturas > 0 ? 'var(--sn-peligro)' : 'var(--sn-exito)' }}>${totalFacturas.toFixed(2)}</strong></div>
                  <div><span style={{ color: 'var(--sn-texto-3)' }}>Promesa vigente hasta</span><br /><strong>{cliente.Promesa_Pago || '—'}</strong></div>
                </div>
                {onuExternalId && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--sn-borde)', fontSize: 12, color: 'var(--sn-texto-3)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    <span>📡 ONU: <strong style={{ color: 'var(--sn-texto-2)' }}>{onuExternalId}</strong></span>
                    <span>🔧 SN: <strong style={{ color: 'var(--sn-texto-2)' }}>{serialOnu}</strong></span>
                  </div>
                )}
              </div>

              {loadingFactura ? (
                <div className="d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" />
                  <span>Consultando factura pendiente…</span>
                </div>
              ) : errorFactura ? (
                <Alert variant="warning" className="mt-2">
                  ⚠️ {errorFactura}
                </Alert>
              ) : factura ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 16, marginBottom: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--sn-texto)' }}>🧾 Factura pendiente</span>
                    <Badge bg={factura.estado.toLowerCase() === 'no pagado' ? 'danger' : 'success'}>
                      {factura.estado}
                    </Badge>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--sn-texto-3)', marginBottom: 4 }}>ID Factura: <strong style={{ color: 'var(--sn-texto-2)' }}>{factura.id}</strong></div>

                  {(() => {
                    // Si la promesa ya se registró con éxito en esta sesión, no mostrar ni los
                    // botones de plazo ni el aviso de bloqueo: solo la tarjeta de éxito de abajo.
                    if (successPromesa) return null;

                    // Regla: solo una promesa de pago cada DIAS_ENTRE_PROMESAS días (22).
                    // Se puede otorgar si NO tiene promesa previa, o si la última venció hace 22 días o más.
                    const dias = diasDesde(cliente.Promesa_Pago);
                    const puedeOtorgar = dias === null || dias >= DIAS_ENTRE_PROMESAS;
                    const estaSuspendido = cliente.estado.toLowerCase() !== 'activo';

                    if (puedeOtorgar) {
                      return (
                        <div style={{ marginTop: 12 }}>
                          <p style={{ fontSize: 13, color: 'var(--sn-texto-2)', marginBottom: 10 }}>
                            ✅ Selecciona el plazo que deseas otorgar al cliente para cumplir su promesa de pago:
                          </p>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                            {[1, 2, 3].map(d => (
                              <button
                                key={d}
                                onClick={() => handlePromesa(d)}
                                disabled={loadingPromesa}
                                style={{
                                  border: '1px solid var(--sn-borde-fuerte)', background: 'var(--sn-info-suave)', color: 'var(--sn-info-texto)',
                                  borderRadius: 12, padding: '12px 6px', cursor: loadingPromesa ? 'wait' : 'pointer',
                                  fontWeight: 700, fontSize: 13, display: 'flex', flexDirection: 'column',
                                  alignItems: 'center', gap: 4, opacity: loadingPromesa ? 0.6 : 1,
                                }}
                              >
                                {loadingPromesa ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <>
                                    <span style={{ fontSize: 20 }}>{d === 1 ? '⏱️' : '📅'}</span>
                                    <span>{d === 1 ? '24 horas' : `${d} días`}</span>
                                  </>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    // El aviso de "ya tienes una promesa activa" SOLO debe aparecer cuando la
                    // promesa está dentro de los DIAS_ENTRE_PROMESAS días Y ADEMÁS el cliente
                    // está suspendido. Si no está suspendido (ya activo), no mostramos nada aquí.
                    if (!estaSuspendido) return null;

                    // Ya usó su promesa en este periodo → mensaje amable + único camino: pagar
                    const proximaFecha = cliente.Promesa_Pago ? sumarDias(cliente.Promesa_Pago, DIAS_ENTRE_PROMESAS) : '';
                    return (
                      <Alert variant="warning" className="mt-3">
                        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
                          ⚠️ Ya tienes una promesa de pago activa
                        </div>
                        <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                          Registramos una promesa de pago vigente hasta el{' '}
                          <strong>{cliente.Promesa_Pago}</strong>. Las promesas de pago solo se pueden
                          otorgar <strong>una vez al mes</strong>, por lo que en este momento no es posible
                          activar otra.
                          {proximaFecha && (
                            <>
                              {' '}Podrás solicitar una nueva promesa a partir del{' '}
                              <strong>{proximaFecha}</strong>.
                            </>
                          )}
                        </div>
                        <div style={{ fontSize: 14, marginTop: 8 }}>
                          Para reactivar tu servicio ahora, realiza el pago de tu factura pendiente. 🙌
                        </div>
                        <Button
                          variant="success"
                          className="mt-3"
                          onClick={() => navigate(`/r/${btoa(cliente.cedula)}`)}
                        >
                          💳 Pagar ahora
                        </Button>
                      </Alert>
                    );
                  })()}

                  {/* ✅ Solo muestra el mensaje real del API */}
                  {errorPromesa && (
                    <Alert variant="danger" className="mt-3">
                      ❌ {errorPromesa}
                    </Alert>
                  )}

                  {successPromesa && (
                    <div style={{ background: 'var(--sn-exito-suave)', border: '1px solid var(--sn-exito)', borderRadius: 12, padding: '14px 16px', marginTop: 14 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--sn-exito-texto)', marginBottom: 6 }}>
                        ✅ Promesa de pago registrada con éxito
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--sn-texto-2)' }}>
                        Fecha límite: <strong>{successPromesa.fecha}</strong>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--sn-texto-2)', marginTop: 2 }}>
                        {successPromesa.descripcion}
                      </div>
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--sn-exito)', fontSize: 13 }}>
                        {recargandoEstado ? (
                          <span style={{ color: 'var(--sn-texto-2)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Spinner animation="border" size="sm" /> Verificando si el servicio ya se activó…
                          </span>
                        ) : cliente.estado.toLowerCase() === 'activo' ? (
                          <div style={{ textAlign: 'center', padding: '4px 0' }}>
                            <div style={{ fontSize: 30 }}>🎉</div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--sn-exito-texto)' }}>
                              ¡Servicio activado correctamente!
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--sn-texto-2)', marginTop: 2 }}>
                              El cliente <strong>{cliente.nombre}</strong> ya está{' '}
                              <Badge bg="success">ACTIVO</Badge> y con conexión.
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div style={{ color: 'var(--sn-texto-2)' }}>
                              Estado actual del servicio:{' '}
                              <Badge bg="warning">{cliente.estado}</Badge>
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--sn-texto-2)', marginTop: 4 }}>
                              La reactivación puede tardar unos minutos. Si no se activa, verifica el pago o vuelve a intentar.
                            </div>
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--sn-exito-texto)', marginTop: 8 }}>
                        Esta ventana se cerrará automáticamente…
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>
      </Container>
      <BottomNav />
    </>
  );
};

export default Promesa;
